/**
 * Pill-OCR perceptual-hash cache (perf/scan-efficiency, 2026-07-06).
 *
 * WHY THIS EXISTS. The 2026-07-06 iPhone 15 Pro device log (ff5f7cb) recorded
 * 1318 `[dex-scan] pill-ocr vision` calls in a single scan session — the vast
 * majority returning the SAME variant string across consecutive frames. The
 * bottom filter-pill row only changes visually when the user taps a different
 * variant tab or switches region; it is FIXED on-screen while scrolling the
 * grid. So the Vision OCR of the pill band is being paid for repeatedly on
 * pixel-identical (or near-identical) input.
 *
 * This module caches the Vision result against a perceptual hash (pHash) of the
 * pill ROI pixels. On the next frame, if the pill ROI pHash is within a small
 * Hamming distance of the cached one, we return the cached Vision result and
 * skip the Vision call entirely.
 *
 * DESIGN (Sam-approved): pHash only, trust the hash. Single-entry cache — we
 * only care about "did the pill change since the LAST frame". No LRU, no
 * multi-slot memory: a stale multi-entry cache could resurrect a wrong read on
 * a visually-similar pill after a tab/region change, which is exactly the
 * correctness hazard the per-session reset guards against.
 *
 * INVARIANT — the cache stores the *Vision result itself*, not a derived
 * heuristic. A cache hit therefore returns byte-identical data to what Vision
 * would have produced on the unchanged pill. It is reset on session restart,
 * region change, and variant-tab change (all correctness-critical: the pill on
 * a new region/variant is a different capsule that might pHash-collide with the
 * previous one). It is NOT reset on scroll — the pill is screen-fixed.
 *
 * Self-contained: no external dependencies. pHash is a standard DCT-style 8x8
 * perceptual hash (32x32 grayscale downscale, mean-threshold bit pattern).
 * Reference: https://en.wikipedia.org/wiki/Perceptual_hashing
 */

import type { VariantChipSignal } from "./pokedexOcr";
import type { ActivePillClassification } from "./activePillClassifier";

/**
 * Whatever `classifyActivePillBand` returns. Kept as a structural alias here so
 * the cache module does not create a runtime import cycle with pokedexOcr
 * (these are type-only imports, erased at compile time).
 */
export interface ActivePillResult {
  chip: VariantChipSignal;
  diag: ActivePillClassification;
}

export interface PillOcrCacheEntry {
  /** 64-bit perceptual hash of the pill ROI. */
  phash: bigint;
  /** Cached Vision result for this ROI (whatever classifyActivePillBand returns). */
  result: ActivePillResult;
  /** How many frames have been served from this entry — telemetry only. */
  framesSinceHit: number;
}

export interface PillOcrCache {
  /**
   * Return the cached result when `phash` is within `hammingThreshold` bits of
   * the current entry, else null. Increments hit/miss + framesSaved counters.
   */
  lookup(phash: bigint, hammingThreshold?: number): ActivePillResult | null;
  /** Replace the current single entry with `phash` + `result`. A result that
   * resolved no variant is not stored: it clears the entry instead. */
  store(phash: bigint, result: ActivePillResult): void;
  /** Clear the cache (session end, region change, variant-tab change). */
  reset(): void;
  /** Telemetry counters. */
  stats(): { hits: number; misses: number; stores: number; framesSaved: number };
  /**
   * Hamming distance from `phash` to the current entry, or null when the cache
   * is empty. Exposed so the wiring can log the distance on both hit and miss
   * without recomputing it.
   */
  distanceToCurrent(phash: bigint): number | null;
  /** The current entry's phash, or null when empty (for `previousPhash` telemetry). */
  currentPhash(): bigint | null;
}

/** Default Hamming distance (out of 64 bits) at/under which a lookup is a hit. */
export const DEFAULT_PILL_PHASH_HAMMING_THRESHOLD = 5;

// bigint constants built via BigInt() rather than `0n`/`1n` literals: the
// project's tsconfig has no `target` set (defaults below ES2020), so bigint
// LITERALS are a compile error (TS2737) while the BigInt runtime + operators
// are available. Using the constructor keeps the module portable to that config
// without touching the shared tsconfig.
const BIG_ZERO = BigInt(0);
const BIG_ONE = BigInt(1);

/**
 * Hamming distance between two 64-bit hashes: popcount of the XOR. Works on
 * bigint so we get the full 64 bits without JS 32-bit bitwise truncation.
 */
export function hammingDistance64(a: bigint, b: bigint): number {
  let x = a ^ b;
  let count = 0;
  while (x !== BIG_ZERO) {
    x &= x - BIG_ONE; // clear the lowest set bit
    count++;
  }
  return count;
}

export function createPillOcrCache(
  opts?: { defaultHammingThreshold?: number },
): PillOcrCache {
  const defaultThreshold =
    opts?.defaultHammingThreshold ?? DEFAULT_PILL_PHASH_HAMMING_THRESHOLD;

  let entry: PillOcrCacheEntry | null = null;
  let hits = 0;
  let misses = 0;
  let stores = 0;
  let framesSaved = 0;

  return {
    lookup(phash: bigint, hammingThreshold: number = defaultThreshold): ActivePillResult | null {
      if (entry === null) {
        misses++;
        return null;
      }
      const dist = hammingDistance64(phash, entry.phash);
      if (dist <= hammingThreshold) {
        hits++;
        framesSaved++;
        entry.framesSinceHit++;
        return entry.result;
      }
      misses++;
      return null;
    },
    store(phash: bigint, result: ActivePillResult): void {
      // NEVER CACHE A READ THAT NAMED NO VARIANT (2026-09-24 hundo-dex log).
      // The pill row is screen-fixed while the grid scrolls, so a cached entry
      // is served for as long as the trainer stays on the page. One empty
      // Vision read, taken while the tapped pill was still animating, was
      // served for all 660 frames of the 100% dex: the Perfect page needs the
      // pill to agree with its peach colour, got "none" every frame, and held
      // every frame (0 numbers read). An unresolved read clears the entry so
      // the next frame asks Vision again; only a real answer is worth reusing.
      if (result.chip.variant == null) {
        entry = null;
        return;
      }
      entry = { phash, result, framesSinceHit: 0 };
      stores++;
    },
    reset(): void {
      entry = null;
    },
    stats() {
      return { hits, misses, stores, framesSaved };
    },
    distanceToCurrent(phash: bigint): number | null {
      if (entry === null) return null;
      return hammingDistance64(phash, entry.phash);
    },
    currentPhash(): bigint | null {
      return entry === null ? null : entry.phash;
    },
  };
}

/**
 * DCT-style 8x8 perceptual hash of an ImageData region.
 *
 * Pipeline (standard pHash, self-contained):
 *   1. Downscale the ROI to 32x32 using box-average sampling over the source
 *      pixels (robust to the exact source resolution — the pill ROI dimensions
 *      vary per device).
 *   2. Convert to grayscale (Rec. 601 luma).
 *   3. Apply a 2D DCT-II and keep the top-left 8x8 low-frequency block.
 *   4. Compute the mean of that block EXCLUDING the DC term (block[0][0]), which
 *      dominates and would bias the threshold.
 *   5. Emit a 64-bit hash where bit i is set when block coefficient i > mean.
 *
 * Returns a bigint so all 64 bits survive (JS bitwise ops truncate to 32 bits).
 */
export function computePillPHash(imageData: ImageData): bigint {
  const SRC_SIZE = 32; // downscale target (grayscale square)
  const HASH_SIZE = 8; // low-frequency block kept for the hash

  const gray = downscaleToGray(imageData, SRC_SIZE);
  const dct = dct2d(gray, SRC_SIZE);

  // Collect the top-left 8x8 low-frequency coefficients.
  const block: number[] = [];
  for (let y = 0; y < HASH_SIZE; y++) {
    for (let x = 0; x < HASH_SIZE; x++) {
      block.push(dct[y * SRC_SIZE + x]);
    }
  }

  // Mean excluding the DC term (index 0).
  let sum = 0;
  for (let i = 1; i < block.length; i++) sum += block[i];
  const mean = sum / (block.length - 1);

  let hash = BIG_ZERO;
  for (let i = 0; i < block.length; i++) {
    if (block[i] > mean) {
      hash |= BIG_ONE << BigInt(i);
    }
  }
  return hash;
}

/**
 * Box-average downscale of an ImageData to `size`x`size` grayscale. Returns a
 * flat `size*size` Float64 array of luma values (0..255).
 */
function downscaleToGray(imageData: ImageData, size: number): Float64Array {
  const { width, height, data } = imageData;
  const out = new Float64Array(size * size);
  // Guard against degenerate ROIs.
  if (width <= 0 || height <= 0) return out;

  for (let oy = 0; oy < size; oy++) {
    const sy0 = Math.floor((oy * height) / size);
    const sy1 = Math.max(sy0 + 1, Math.floor(((oy + 1) * height) / size));
    for (let ox = 0; ox < size; ox++) {
      const sx0 = Math.floor((ox * width) / size);
      const sx1 = Math.max(sx0 + 1, Math.floor(((ox + 1) * width) / size));
      let acc = 0;
      let n = 0;
      for (let sy = sy0; sy < sy1 && sy < height; sy++) {
        for (let sx = sx0; sx < sx1 && sx < width; sx++) {
          const idx = (sy * width + sx) * 4;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          // Rec. 601 luma.
          acc += 0.299 * r + 0.587 * g + 0.114 * b;
          n++;
        }
      }
      out[oy * size + ox] = n > 0 ? acc / n : 0;
    }
  }
  return out;
}

/**
 * Separable 2D DCT-II over an NxN grayscale array. Returns a flat NxN array of
 * coefficients. Cosine bases are cached per N so repeated calls (every frame)
 * do not recompute them.
 */
const dctBasisCache = new Map<number, Float64Array>();

function dctBasis(n: number): Float64Array {
  const cached = dctBasisCache.get(n);
  if (cached) return cached;
  // basis[u * n + x] = cos(((2x + 1) * u * PI) / (2n))
  const basis = new Float64Array(n * n);
  for (let u = 0; u < n; u++) {
    for (let x = 0; x < n; x++) {
      basis[u * n + x] = Math.cos(((2 * x + 1) * u * Math.PI) / (2 * n));
    }
  }
  dctBasisCache.set(n, basis);
  return basis;
}

function dct2d(input: Float64Array, n: number): Float64Array {
  const basis = dctBasis(n);
  // Row pass.
  const rows = new Float64Array(n * n);
  for (let y = 0; y < n; y++) {
    for (let u = 0; u < n; u++) {
      let acc = 0;
      for (let x = 0; x < n; x++) {
        acc += input[y * n + x] * basis[u * n + x];
      }
      rows[y * n + u] = acc;
    }
  }
  // Column pass.
  const out = new Float64Array(n * n);
  for (let x = 0; x < n; x++) {
    for (let v = 0; v < n; v++) {
      let acc = 0;
      for (let y = 0; y < n; y++) {
        acc += rows[y * n + x] * basis[v * n + y];
      }
      out[v * n + x] = acc;
    }
  }
  return out;
}
